import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import {
  buildGroupSalesParticipantRows,
  buildGroupSalesSaleItemRows,
  formatGroupSalesNumber,
  isGroupSalesAmountExpand,
  type GroupSalesSummary,
} from '@/utils/approval/groupSales';
import { GroupSalesRoot } from './styles';

const TABLE_CLASS =
  'table table-bordered table-striped table-responsive no-margin';

type Props = { summary: GroupSalesSummary; t: TFunction };

/** 레거시 담당자/대상자 표 — 금액단체만 할인율 칸 */
export const ParticipantTable = ({
  summary,
  t,
  isAmount,
}: {
  summary: Pick<GroupSalesSummary, 'participants'>;
  t: TFunction;
  isAmount: boolean;
}) => {
  const num = formatGroupSalesNumber;
  const rows = buildGroupSalesParticipantRows(summary.participants ?? []);
  return (
    <div>
      <table className={TABLE_CLASS}>
        <tbody>
          <tr>
            <td className="text-center">{t('고객 CID')}</td>
            <td className="text-center">{t('이름')}</td>
            <td className="text-center">{t('구매금액')}</td>
            <td className="text-center">{t('적립포인트')}</td>
            <td className="text-center">{t('진행횟수')}</td>
            {isAmount && <td className="text-center">{t('할인율')}</td>}
            <td className="text-center">{t('과거 진행일자')}</td>
            <td className="text-center">{t('업체명')}</td>
            <td className="text-center">{t('매장명')}</td>
          </tr>
          {rows.map(({ row, rowSpan, hasHistory }, i) => (
            <tr
              key={i}
              className={hasHistory ? 'group-sales-history' : undefined}
            >
              {rowSpan > 0 && (
                <>
                  <td className="text-center" rowSpan={rowSpan}>
                    {row.customerId}
                  </td>
                  <td className="text-center" rowSpan={rowSpan}>
                    {row.name}
                  </td>
                  <td className="text-right" rowSpan={rowSpan}>
                    {num(row.purchaseAmount)}
                  </td>
                  <td className="text-right" rowSpan={rowSpan}>
                    {num(row.earnedPoint)}
                  </td>
                  <td className="text-center" rowSpan={rowSpan}>
                    {row.progressCount}
                  </td>
                </>
              )}
              {/* 레거시: 진행 0회면 뒤 칸을 빈칸 하나로, 과거 진행일자가 없으면 뒤 칸 없음 */}
              {!Number(row.progressCount) ? (
                <td className="text-center" colSpan={isAmount ? 4 : 3} />
              ) : (
                row.lastProgressDate && (
                  <>
                    {isAmount && (
                      <td className="text-center">{`${
                        row.discountRate ?? ''
                      }%`}</td>
                    )}
                    <td className="text-center">{row.lastProgressDate}</td>
                    <td className="text-center">{row.organizationName}</td>
                    <td className="text-center">{row.shopName}</td>
                  </>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** 레거시 상세내역(적용 대상 스타일 리스트) */
export const SaleItemTable = ({
  summary,
  t,
}: {
  summary: Pick<GroupSalesSummary, 'saleItems' | 'styleCount'>;
  t: TFunction;
}) => {
  const num = formatGroupSalesNumber;
  const items = summary.saleItems ?? [];
  if (!items.length) return null;
  const { rows, total } = buildGroupSalesSaleItemRows(items);
  const totalCells = (totals: typeof total) => (
    <>
      <th className="text-center">{num(totals.quantity)}</th>
      <th className="text-right">{num(totals.amount)}</th>
      <th className="text-center" />
      <th className="text-right">{num(totals.discountAmount)}</th>
      <th className="text-right">{num(totals.saleAmount)}</th>
    </>
  );
  const remarks = items.filter((item) => item.remarks);
  return (
    <>
      <div>
        <table className={TABLE_CLASS}>
          <tbody>
            <tr>
              <th className="text-left" colSpan={10}>
                {t('적용 대상 스타일 리스트')}
              </th>
            </tr>
            <tr>
              <td className="text-center">{t('시즌')}</td>
              <td className="text-center">{t('판매가 유형')}</td>
              <td className="text-center">{t('스타일')}</td>
              <td className="text-center">{t('단가')}</td>
              <td className="text-center">{t('현단가')}</td>
              <td className="text-center">{t('수량')}</td>
              <td className="text-center">{t('현단가 총계')}</td>
              <td className="text-center">{t('할인율')}</td>
              <td className="text-center">{t('할인금액')}</td>
              <td className="text-center">{t('실판매가')}</td>
            </tr>
            {rows.map((r, i) =>
              r.kind === 'item' ? (
                <tr key={i}>
                  <td className="text-center">{r.item.season}</td>
                  <td className="text-center">{r.item.saleTypeName}</td>
                  <td className="text-center">{r.item.partCode}</td>
                  <td className="text-right">{num(r.item.tagPrice)}</td>
                  <td className="text-right">{num(r.item.unitPrice)}</td>
                  <td className="text-center">{num(r.item.quantity)}</td>
                  <td className="text-right">{num(r.item.amount)}</td>
                  <td className="text-center">{`${
                    r.item.discountRate ?? ''
                  }%`}</td>
                  <td className="text-right">{num(r.item.discountAmount)}</td>
                  <td className="text-right">{num(r.item.saleAmount)}</td>
                </tr>
              ) : (
                <tr key={i}>
                  <th className="text-center" colSpan={5}>
                    {t('계')}
                  </th>
                  {totalCells(r.totals)}
                </tr>
              ),
            )}
            <tr>
              <th className="text-center" colSpan={5}>
                {t('합계')}
              </th>
              {totalCells(total)}
            </tr>
            <tr>
              <th className="text-right" colSpan={10}>
                {t('총 {{count}} 스타일', {
                  count: Number(summary.styleCount) || 0,
                })}
              </th>
            </tr>
          </tbody>
        </table>
      </div>
      {remarks.length > 0 && (
        <div className="group-sales-notice">
          {remarks.map((item, i) => (
            <div key={i}>{`- ${item.partCode ?? ''} : ${item.remarks}`}</div>
          ))}
        </div>
      )}
    </>
  );
};

type ToggleProps = {
  isAmount: boolean;
  participants: ReactNode;
  detail: ReactNode;
  t: TFunction;
};

/** 레거시 펼침 버튼 — 담당자/대상자 내역은 펼친 채, 상세내역은 닫힌 채 시작 */
export const GroupSalesExpandToggle = ({
  isAmount,
  participants,
  detail,
  t,
}: ToggleProps) => {
  const [participantsOpen, setParticipantsOpen] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const participantButton = isAmount
    ? participantsOpen
      ? '담당자 내역 닫기'
      : '담당자 내역 보기'
    : participantsOpen
    ? '대상자 내역 닫기'
    : '대상자 내역 보기';
  return (
    <>
      <ButtonRow>
        <BoxButton
          type="button"
          variant="secondary"
          size="small"
          onClick={() => setParticipantsOpen((v) => !v)}
        >
          {t(participantButton)}
        </BoxButton>
        {isAmount && (
          <BoxButton
            type="button"
            variant="secondary"
            size="small"
            onClick={() => setDetailOpen((v) => !v)}
          >
            {t(detailOpen ? '상세내역 닫기' : '상세내역 보기')}
          </BoxButton>
        )}
      </ButtonRow>
      {participantsOpen && participants}
      {isAmount && detailOpen && detail}
    </>
  );
};

/** 저장 HTML 용 — 버튼 없이 내역만. 조회 화면이 class 로 찾아 버튼을 다시 붙임 */
export const GroupSalesExpandSections = ({ summary, t }: Props) => {
  if (!summary.header) return null;
  const isAmount = isGroupSalesAmountExpand(summary.header);
  return (
    <div className="group-sales-expand" data-sale-type={isAmount ? 'A' : 'B'}>
      <div className="group-sales-participants">
        {(summary.participants ?? []).length > 0 && (
          <ParticipantTable summary={summary} t={t} isAmount={isAmount} />
        )}
      </div>
      {isAmount && (
        <div className="group-sales-detail">
          <SaleItemTable summary={summary} t={t} />
        </div>
      )}
    </div>
  );
};

/** 작성 화면 — 방금 불러온 내역 */
const GroupSalesExpand = ({ summary, t }: Props) => {
  if (!summary.header) return null;
  const isAmount = isGroupSalesAmountExpand(summary.header);
  return (
    <GroupSalesRoot>
      <GroupSalesExpandToggle
        isAmount={isAmount}
        participants={
          (summary.participants ?? []).length > 0 && (
            <ParticipantTable summary={summary} t={t} isAmount={isAmount} />
          )
        }
        detail={<SaleItemTable summary={summary} t={t} />}
        t={t}
      />
    </GroupSalesRoot>
  );
};

const ButtonRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  margin: '8px 0',
});

export default GroupSalesExpand;
